import { FC, useState } from "react";
import FullScreenDialog from "@components/FullScreenDialog";
import { Team, TeamReference } from "@/models/pdfData";
import { gameState, useGameState } from "@/services/gameState";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListSubheader,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  ExtendedOfficial,
  ExtendedPlayer,
  OfficialFunction,
  PlayerPosition,
} from "@/models/socketIo/initializeMatchPayload";
import { TeamSquad } from "@/models/gameGeneral";
import { Check } from "@mui/icons-material";
import { useResponsiveContext } from "@context/ResponsiveContext";
import { Teams } from "@/models/gameState";

const positionMap: { [key in PlayerPosition]: string } = {
  WING_SPIKER: "Außenangreifer",
  SETTER: "Zuspieler",
  LIBERO: "Libero",
  MIDDLE_BLOCKER: "Mittelblocker",
  OPPOSITE: "Diagonalangreifer",
};

const functionMap: { [key in OfficialFunction]: string } = {
  TRAINER: "Trainer",
  TRAINER_ASSISTANT: "Co-Trainer",
  TRAINER_ASSISTANT_SCOUT: "Co-Trainer (Scout)",
  PHYSIOTHERAPIST: "Physiotherapeut",
  PHYSICIAN: "Arzt",
} as const;

const functionOrder: { [key in OfficialFunction]: number } = {
  TRAINER: 1,
  TRAINER_ASSISTANT: 2,
  TRAINER_ASSISTANT_SCOUT: 3,
  PHYSIOTHERAPIST: 4,
  PHYSICIAN: 5,
};

const PersonAvatar: FC<{
  person: ExtendedPlayer | ExtendedOfficial;
  inSquad: boolean | undefined;
}> = ({ person, inSquad }) => {
  const photoUrl = person.portraitPhotoMax600x600?.url;

  return (
    <ListItemAvatar sx={{ pr: "2vw" }}>
      <Avatar
        src={photoUrl}
        alt={`${person.firstName} ${person.lastName}`}
        sx={{
          width: "clamp(60px, 10vw, 100px)",
          height: "clamp(60px, 10vw, 100px)",
        }}
        slotProps={{
          img: {
            style: {
              objectPosition: "center -10%",
              transform: "scale(1.2)",
              opacity: inSquad === false ? 0.66 : 1,
            },
          },
        }}
      >{`${person.firstName.substring(0, 1)}${person.lastName.substring(0, 1)}`}</Avatar>
    </ListItemAvatar>
  );
};

const PlayerListItem: FC<{ player: ExtendedPlayer; teamSquad: TeamSquad }> = ({
  player,
  teamSquad,
}) => {
  const inSquad = isInSquad(player, teamSquad);

  const isGameCaptain =
    teamSquad.confirmed && teamSquad.captain?.uuid === player.uuid;

  return (
    <ListItem key={player.jerseyNumber}>
      <PersonAvatar person={player} inSquad={inSquad} />
      <ListItemText
        slotProps={{
          primary: { color: inSquad === false ? "text.disabled" : "inherit" },
          secondary: {
            color: inSquad === false ? "text.disabled" : "text.secondary",
            pl: "55px",
          },
        }}
        primary={
          <Box
            display={"grid"}
            alignItems={"center"}
            gap={"5px"}
            gridTemplateColumns={"50px auto"}
          >
            <Typography
              sx={{
                textAlign: "center",
                textDecoration: isGameCaptain ? "underline" : null,
              }}
            >
              {`${player.jerseyNumber}${isGameCaptain ? " (C) " : ""}`}
            </Typography>
            <Typography>{`${player.firstName} ${player.lastName}`}</Typography>
          </Box>
        }
        secondary={
          player.position
            ? positionMap[player.position] || player.position
            : player.position
        }
      />
    </ListItem>
  );
};

const OfficialListItem: FC<{
  official: ExtendedOfficial;
  teamSquad: TeamSquad;
}> = ({ official, teamSquad }) => {
  const inSquad = isInSquad(official, teamSquad);

  return (
    <ListItem key={official.uuid}>
      <PersonAvatar person={official} inSquad={inSquad} />
      <ListItemText
        slotProps={{
          primary: { color: inSquad === false ? "text.disabled" : "inherit" },
          secondary: {
            color: inSquad === false ? "text.disabled" : "text.secondary",
          },
        }}
        primary={`${official.firstName} ${official.lastName}`}
        secondary={
          official.function
            ? functionMap[official.function] || official.function
            : official.function
        }
      />
    </ListItem>
  );
};

const bySquadStatusAndJerseyNumber =
  (teamSquad: TeamSquad) => (p1: ExtendedPlayer, p2: ExtendedPlayer) => {
    const p1InSquad = isInSquad(p1, teamSquad);
    const p2InSquad = isInSquad(p2, teamSquad);

    if (p1InSquad && !p2InSquad) {
      return -1;
    } else if (!p1InSquad && p2InSquad) {
      return 1;
    } else {
      try {
        return (
          Number.parseInt(p1.jerseyNumber) - Number.parseInt(p2.jerseyNumber)
        );
      } catch {
        return p1.lastName.localeCompare(p2.lastName);
      }
    }
  };

const bySquadStatusAndOfficialRank =
  (teamSquad: TeamSquad) => (o1: ExtendedOfficial, o2: ExtendedOfficial) => {
    const o1InSquad = isInSquad(o1, teamSquad);
    const o2InSquad = isInSquad(o2, teamSquad);

    if (o1InSquad && !o2InSquad) {
      return -1;
    } else if (!o1InSquad && o2InSquad) {
      return 1;
    } else {
      return functionOrder[o1.function] - functionOrder[o2.function];
    }
  };

const isInSquad = (
  person: ExtendedPlayer | ExtendedOfficial,
  teamSquad: TeamSquad,
): boolean | undefined => {
  if (!teamSquad.confirmed) {
    return undefined;
  }
  if ("jerseyNumber" in person) {
    return teamSquad.players.some((p) => p.uuid === person.uuid);
  } else {
    return teamSquad.officials.some((o) => o.uuid === person.uuid);
  }
};

const PlayerList: FC<{ teamRef: TeamReference; team: Teams["team1"] }> = ({
  teamRef,
  team,
}) => {
  const { onTablet } = useResponsiveContext();
  const teamSquad = gameState("teamSquads")[teamRef];

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      sx={{ timelineScope: "--stuck-trigger" }}
    >
      {team?.teamPhotoMax1000x1000?.url && (
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            maxHeight: "30vw",
            overflow: "hidden",
          }}
        >
          <img
            src={team.teamPhotoMax1000x1000.url}
            style={{
              aspectRatio: "2/1",
              objectFit: "cover",
              objectPosition: "top center",
              maxWidth: "100%",
              height: "100%",
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          viewTimelineName: "--stuck-trigger",
          height: "1px",
          marginBottom: "-1px",
          opacity: 0,
        }}
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "15vw 1fr",
          alignItems: "center",
          position: onTablet ? "inherit" : "sticky",
          top: 0,
          bgcolor: "background.paper",
          zIndex: 2,
          padding: "clamp(2px,1vw,8px)",
          borderBottom: `1px solid transparent`,
          animation: "slide-border linear forwards",
          animationTimeline: "--stuck-trigger",
          animationRange: "exit 0% exit 100%",
          "@keyframes slide-border": (theme) => ({
            to: {
              borderBottomColor: theme.palette.divider,
            },
          }),
        }}
      >
        <Avatar
          src={team.logoImageMax400x400.url}
          sx={{
            flex: "0 0 auto",
            width: "clamp(50px,12vw,100px)",
            height: "clamp(50px,12vw,100px)",
          }}
          alt={team.clubCode}
        />
        <Typography align={"left"} noWrap textOverflow={"clip"}>
          {team.name}
        </Typography>
      </Box>
      <List
        sx={{
          maxHeight: "100%",
          paddingTop: 0,
        }}
      >
        <li key={`section-players`}>
          <ul style={{ padding: 0 }}>
            <ListSubheader
              sx={{
                top: onTablet ? 0 : "clamp(54px, 13vw, 116px)",
                textAlign: "center",
                animation: "slide-border linear forwards",
                animationTimeline: "--stuck-trigger",
                animationRange: "exit 0% exit 100%",
                marginBottom: "-1px",
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                "@keyframes slide-border": (theme) => ({
                  to: {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    filter: "drop-shadow(2px 4px 6px #00000011)",
                  },
                }),
              }}
            >
              {`Spieler${teamSquad.confirmed ? ` (${teamSquad.players.length})` : ""}`}
            </ListSubheader>
            {team.players
              .sort(bySquadStatusAndJerseyNumber(teamSquad))
              .map((player) => (
                <PlayerListItem
                  key={player.uuid}
                  player={player}
                  teamSquad={teamSquad}
                />
              ))}
          </ul>
        </li>
        <li key={`section-officials`}>
          <ul style={{ padding: 0 }}>
            <ListSubheader
              sx={{
                top: onTablet ? 0 : "clamp(54px, 13vw, 116px)",
                textAlign: "center",
                animation: "slide-border linear forwards",
                animationTimeline: "--stuck-trigger",
                animationRange: "exit 0% exit 100%",
                marginBottom: "-1px",
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                "@keyframes slide-border": (theme) => ({
                  to: {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    boxShadow: "2px 4px 6px #00000011",
                  },
                }),
              }}
            >
              {`Offizielle${teamSquad.confirmed ? ` (${teamSquad.officials.length})` : ""}`}
            </ListSubheader>
            {team.officials
              .sort(bySquadStatusAndOfficialRank(teamSquad))
              .map((official) => (
                <OfficialListItem
                  key={official.uuid}
                  official={official}
                  teamSquad={teamSquad}
                />
              ))}
          </ul>
        </li>
      </List>
    </Box>
  );
};

const PlayerLists: FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => {
  const teams = gameState("teams");
  const { onTablet } = useResponsiveContext();
  const [tabSelection, setTabSelection] = useState<TeamReference>("team1");

  return (
    <FullScreenDialog show={open} handleClose={onClose} title={"Spielerlisten"}>
      {onTablet ? (
        <Box display={"flex"} flexDirection={"column"} height={"100%"}>
          <AppBar position="static" color="default">
            <Tabs
              value={tabSelection}
              onChange={(_, newValue) => setTabSelection(newValue)}
              variant="fullWidth"
            >
              <Tab label={teams["team1"].shortName} value="team1" />
              <Tab label={teams["team2"].shortName} value="team2" />
            </Tabs>
          </AppBar>
          <Box height={"100%"} overflow={"auto"}>
            <PlayerList teamRef={tabSelection} team={teams[tabSelection]} />
          </Box>
        </Box>
      ) : (
        <Box
          display={"grid"}
          gridTemplateColumns={"1fr auto 1fr"}
          height={"100%"}
          overflow={"auto"}
        >
          <PlayerList teamRef="team1" team={teams["team1"]} />
          <Divider orientation="vertical" flexItem />
          <PlayerList teamRef="team2" team={teams["team2"]} />
        </Box>
      )}
    </FullScreenDialog>
  );
};

export default PlayerLists;
